import React from 'react';
import ReactDOM from 'react-dom';

import { designTokens } from '@project44/manifest-ds';
import { mapboxgl } from '@project44/ui-utils';
import {
  GeofenceResponse,
  TrackedShipmentPosition,
} from '@project44-models/shipment-entity';

import { Position } from '../../../../utils/helpers/shipmentMap';
import PingsToggle from './components/PingsToggle';
import { Popup } from './components/Popup';
import Zoom from './components/Zoom';

type Ref = { current: null | HTMLElement };

const setCursorPointer = (map: mapboxgl.Map) =>
  (map.getCanvas().style.cursor = 'pointer');

const resetCursor = (map: mapboxgl.Map) => (map.getCanvas().style.cursor = '');

export const createMarkerReference = (component: JSX.Element): HTMLElement => {
  const ref: Ref = React.createRef();
  ref.current = document.createElement('div');
  ReactDOM.render(component, ref.current);
  return ref.current;
};

export const drawRoute = (
  map: mapboxgl.Map,
  id: string | undefined,
  coordinates: [number, number][],
  idx: number,
) => {
  const identity = `route-${idx}-${id}`;

  map.addSource(identity, {
    type: 'geojson',
    data: {
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'LineString',
        coordinates: coordinates,
      },
    },
  });

  map.addLayer({
    id: identity,
    type: 'line',
    source: identity,
    layout: {
      'line-join': 'round',
      'line-cap': 'round',
    },
    paint: {
      'line-color': designTokens.colorPaletteIndigo500,
      'line-width': 3,
    },
  });
};

export const drawPopup = (
  map: mapboxgl.Map,
  props: TrackedShipmentPosition,
  onPointClick: (p: TrackedShipmentPosition) => void,
) => {
  const popup = new mapboxgl.Popup({
    closeButton: false,
    closeOnClick: true,
  });

  const pointLayers = map
    .getStyle()
    .layers.filter(
      (l) => l.type === 'circle' && l.id.startsWith('unclustered-point-'),
    );

  pointLayers.forEach((l) => {
    map.setPaintProperty(l.id, 'circle-color', [
      'case',
      ['==', ['get', 'dateTime'], props?.dateTime],
      designTokens.colorPaletteIndigo500,
      designTokens.colorPaletteGrey100,
    ]);
  });

  popup
    .setLngLat([Number(props?.longitude), Number(props?.latitude)])
    .setDOMContent(
      createMarkerReference(
        <Popup
          dateTime={String(props?.dateTime)}
          coordinates={[Number(props?.longitude), Number(props?.latitude)]}
          onClose={popup.remove}
        />,
      ),
    )
    .addTo(map);

  popup.on('close', () => {
    pointLayers.forEach((l) => {
      map.setPaintProperty(
        l.id,
        'circle-color',
        designTokens.colorPaletteGrey100,
      );
    });

    onPointClick({} as TrackedShipmentPosition);
  });
  map.on('closeAllPopups', popup.remove);
};

export const drawPoints = (
  map: mapboxgl.Map,
  coordinates: Position[],
  idx: string,
  onPointClick: (p: TrackedShipmentPosition) => void,
) => {
  const identity = `points-${idx}`;

  map.addSource(identity, {
    type: 'geojson',
    cluster: true,
    clusterMinPoints: 3,
    clusterRadius: 25,
    data: {
      type: 'FeatureCollection',
      features: coordinates.map((coord) => ({
        type: 'Feature',
        properties: {
          dateTime: coord.dateTime,
          latitude: coord.lat,
          longitude: coord.lon,
        },
        geometry: {
          type: 'Point',
          coordinates: [coord.lon, coord.lat],
        },
      })),
    },
  });

  map.addLayer({
    id: `clusters-${idx}`,
    type: 'circle',
    source: identity,
    filter: ['has', 'point_count'],
    paint: {
      'circle-color': designTokens.colorPaletteGrey100,
      'circle-stroke-color': designTokens.colorPaletteIndigo500,
      'circle-stroke-width': 2,
      'circle-radius': 10,
    },
  });

  map.addLayer({
    id: `cluster-count-${idx}`,
    type: 'symbol',
    source: identity,
    filter: ['has', 'point_count'],
    layout: {
      'text-field': '{point_count_abbreviated}',
      'text-size': 12,
    },
    paint: {
      'text-color': '#16158D',
    },
  });

  map.addLayer({
    id: `unclustered-point-${idx}`,
    type: 'circle',
    source: identity,
    filter: ['!', ['has', 'point_count']],
    paint: {
      'circle-color': designTokens.colorPaletteGrey100,
      'circle-stroke-color': designTokens.colorPaletteIndigo500,
      'circle-stroke-width': 2,
      'circle-radius': 4,
    },
  });

  const zoomUntil = (
    point: mapboxgl.PointLike,
    center: [number, number],
    clusterSource: mapboxgl.AnySourceImpl,
    clusterId: number,
  ) => {
    const ff =
      map.queryRenderedFeatures(point, { layers: [`clusters-${idx}`] }) || [];
    if (ff.some((f) => f.properties?.cluster)) {
      map.easeTo({ center, zoom: map.getZoom() + 2 });
      setTimeout(() => zoomUntil(point, center, clusterSource, clusterId), 100);
    }
  };

  map.on('click', `clusters-${idx}`, (e) => {
    const features =
      map.queryRenderedFeatures(e.point, { layers: [`clusters-${idx}`] }) || [];
    const clusterId = features[0]?.properties?.cluster_id;
    const clusterSource = map.getSource(identity);

    const center = (features[0]?.geometry as GeoJSON.Point)?.coordinates as [
      number,
      number,
    ];
    zoomUntil(e.point, center, clusterSource, clusterId);
  });

  map.on('click', `unclustered-point-${idx}`, (e) => {
    const props = (e.features || [])[0].properties;
    drawPopup(map, props as TrackedShipmentPosition, onPointClick);
    onPointClick(props as TrackedShipmentPosition);
  });

  map.on('mouseenter', `clusters-${idx}`, () => setCursorPointer(map));
  map.on('mouseenter', `unclustered-point-${idx}`, () => setCursorPointer(map));

  map.on('mouseleave', `clusters-${idx}`, () => resetCursor(map));
  map.on('mouseleave', `unclustered-point-${idx}`, () => resetCursor(map));
};

export const drawMarker = (
  map: mapboxgl.Map,
  child: JSX.Element,
  coords: [number, number],
) => {
  const marker = createMarkerReference(child);
  new mapboxgl.Marker(marker).setLngLat(coords).addTo(map);
};

export const fitBounds = (
  map: mapboxgl.Map,
  coords: [number, number][],
  maxLabelLength = 0,
) => {
  const padding = 50;
  if (!coords.length) return;

  const bounds = new mapboxgl.LngLatBounds();
  coords.forEach((coord) => bounds.extend(coord));

  map.fitBounds(bounds, {
    padding: {
      top: padding,
      bottom: padding,
      left: padding,
      right: Math.max(padding, maxLabelLength * 10),
    },
  });
};

export const drawGeofence = (map: mapboxgl.Map, geofence: GeofenceResponse) => {
  const { locationId: id, geometry } = geofence;
  const existing = map.getSource(`geofence-${id}`);

  if (existing) {
    map.removeLayer(`geofence-polygon-${id}`);
    map.removeLayer(`geofence-outline-${id}`);
    map.removeSource(`geofence-${id}`);
  }

  map.addSource(`geofence-${id}`, {
    type: 'geojson',
    data: {
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'Polygon',
        coordinates: [geometry],
      },
    },
  });

  map.addLayer({
    id: `geofence-polygon-${id}`,
    type: 'fill',
    source: `geofence-${id}`,
    layout: {},
    paint: {
      'fill-color': '#0072ec',
      'fill-opacity': 0.1,
    },
  });

  map.addLayer({
    id: `geofence-outline-${id}`,
    type: 'line',
    source: `geofence-${id}`,
    layout: {},
    paint: {
      'line-color': '#0072ec',
      'line-width': 2,
    },
  });
};

export const drawPingsControl = (
  map: mapboxgl.Map,
  onClick: () => void,
  disabled: boolean,
) => {
  map.addControl(new PingsToggle({ onClick, disabled }), 'top-right');
};

export const drawZoomControls = (map: mapboxgl.Map) => {
  map.addControl(
    new Zoom({
      onZoomIn: () => map.zoomIn(),
      onZoomOut: () => map.zoomOut(),
    }),
    'top-right',
  );
};
